import { useAppDispatch } from "@/app/hooks";
import PropertyFields, {
  PropertyDisplayLabels,
} from "@/constants/propertyFields";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import { EmeraldButtonSize, EmeraldButtonVariant } from "@emerald/nxcore";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useSurvey } from "@hooks/useSurveySelectors";
import {
  useAddCustomFieldMutation,
  useDeleteCustomFieldMutation,
  useUpdateCustomFieldMutation,
} from "@services/survey-customFields";
import { AddOrEditCustomFieldInputProps } from "@shared/types/component.types";
import {
  addCustomField as addCustomFieldAction,
  deleteCustomField as deleteCustomFieldAction,
  updateCustomField as updateCustomFieldAction,
} from "@survey/surveySlice";
import { useState } from "react";

const AddOrEditCustomFieldInput = (props: AddOrEditCustomFieldInputProps) => {
  const { eachField, onClose } = props;
  const currentSurvey = useSurvey();
  const [deleteCustomField] = useDeleteCustomFieldMutation();
  const [addCustomField] = useAddCustomFieldMutation();
  const [updateCustomField] = useUpdateCustomFieldMutation();
  const { quickDelete, withLoading } = useSimpleConfirmation();
  const [customLabel, setCustomLabel] = useState(eachField.label);
  const [errorText, setErrorText] = useState("");
  const dispatch = useAppDispatch();
  const propertyFieldLabels = [
    ...PropertyFields.map((f) => f.label.toLowerCase()),
    ...PropertyDisplayLabels.map((l) => l.toLowerCase()),
    ...(currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.map(
      (r) => r.fieldLabel.toLowerCase(),
    ) ?? []),
  ];
  const customFieldLabels = [
    ...new Set([
      ...propertyFieldLabels,
      ...(currentSurvey?.customFields
        .filter((field) => field.customFieldId !== eachField.id)
        .map((field) => field.name?.toLowerCase())
        .filter(Boolean) ?? []),
    ]),
  ];

  const handleDeleteCustomField = () => {
    quickDelete({
      title: `"${eachField.label || "Custom Field"}"`,
      text: "Are you sure you want to delete this custom field from all properties in this survey?",
    })
      .then((confirmed) => {
        if (confirmed) {
          dispatch(deleteCustomFieldAction(eachField.id));
          withLoading(
            () =>
              deleteCustomField({
                id: eachField.id,
              }).unwrap(),
            {
              loadingText: "Deleting Custom Field...",
              successText: "Custom Field deleted successfully!",
              errorText: "Failed to delete Custom Field.",
            },
          )
            .then(() => {
              onClose?.();
            })
            .catch((_error) => {
              console.error("Error deleting custom field");
            });
        }
      })
      .catch((_error) => {
        console.error("Error with confirmation dialog");
      });
  };

  const isLabelMatching = () => {
    if (customLabel.trim() === "") {
      setErrorText("This field is required.");
      return false;
    }

    if (customFieldLabels?.includes(customLabel.toLowerCase())) {
      setErrorText("This field already exists.");
      return false;
    }

    setErrorText("");
    return true;
  };

  const handleAddCustomField = () => {
    // Logic to add a new custom field label
    if (isLabelMatching() === false) return;

    onClose?.();
    if (eachField.id === "new" && currentSurvey?.id) {
      addCustomField({
        data: {
          surveyId: currentSurvey.id,
          name: customLabel,
        },
      })
        .then((res) => {
          dispatch(addCustomFieldAction(res.data));
        })
        .catch((_error) => {
          console.error("Error adding custom field");
        });
    } else {
      // Logic to update custom field label
      dispatch(
        updateCustomFieldAction({
          customFieldId: eachField.id,
          name: customLabel,
        }),
      );
      updateCustomField({
        data: {
          customFieldId: eachField.id,
          name: customLabel,
        },
      })
        .then(() => {
          // Update successful
        })
        .catch((_error) => {
          console.error("Error updating custom field");
        });
    }
  };

  return (
    <div className="bg-secondary-lightest p-4 rounded-lg">
      <EmeraldInputV2
        type="text"
        label="Custom Field Label"
        value={customLabel}
        size="small"
        className={"without-label"}
        isValid={errorText === ""}
        errorText={errorText}
        placeholder="Custom Field Label"
        onChange={(e) => {
          setCustomLabel(e.target.value);
          if (e.target.value === "") setErrorText("");
        }}
      />
      <div
        className={`flex mt-4 gap-4 ${eachField.id === "new" ? "justify-end" : "justify-between"}`}
      >
        {eachField.id !== "new" && (
          <EmeraldButton
            label="Delete"
            type={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            nativeProps={{
              title: "Delete",
              type: "button",
              "aria-labelledby": "delete-custom-field",
              "aria-label": "Delete Custom Field",
            }}
            onClick={handleDeleteCustomField}
          />
        )}
        <div className="flex gap-4 flex-end">
          <EmeraldButton
            label="Cancel"
            type={EmeraldButtonVariant.TextButton}
            size={EmeraldButtonSize.Medium}
            nativeProps={{
              title: "Cancel",
              type: "button",
              "aria-labelledby": "cancel-custom-field",
              "aria-label": "Cancel Custom Field",
            }}
            onClick={onClose}
          />
          <EmeraldButton
            label="Save"
            type={EmeraldButtonVariant.Primary}
            size={EmeraldButtonSize.Medium}
            nativeProps={{
              title: "Save",
              type: "button",
              "aria-labelledby": "save-custom-field",
              "aria-label": "Save Custom Field",
            }}
            onClick={handleAddCustomField}
          />
        </div>
      </div>
    </div>
  );
};

export default AddOrEditCustomFieldInput;
